import classNames from 'classnames';
import NextImage from 'next/image';
import Icon from '@utilities/icon';
import { colors } from '@configs/Theme';
import { colorPallete } from '@configs/color-pallete';
import { DoctorActivityStatusEnum } from '@models/ocs/doctor';
import DoctorInfoInsurances from './doctor-info-insurances';
interface DoctorInfoProps {
  avatarSrc: string;
  name: string;
  expertise: string;
  medicalCodeSystem?: string;
  historyCount?: number;
  rootClassName?: string;
  activity?: DoctorActivityStatusEnum;
  clinicCities?: string[];
  avatarSize?: {
    width: number;
    height: number;
  };
  doctorId?: number;
}
const DoctorInfo = ({
  avatarSrc,
  name,
  expertise,
  medicalCodeSystem,
  historyCount,
  activity,
  clinicCities,
  rootClassName,
  avatarSize = {
    width: 72,
    height: 72,
  },
  doctorId,
}: DoctorInfoProps) => {
  const SPACE_REGEX = /\u00A0/g;
  const normalizedExpertise = expertise?.replace(SPACE_REGEX, ' ');
  const isOnline = DoctorActivityStatusEnum.Offline !== activity;

  return (
    <div className="px-4 pt-1">
      <div className={classNames('flex', rootClassName)}>
        <div className="w-full flex justify-between">
          <div className="flex w-full gap-x-2">
            <div className="relative">
              <div
                className={classNames(
                  'relative flex items-center justify-center rounded-full overflow-hidden border object-contain',
                  isOnline ? 'border-green-300' : 'border-surface-tertiary',
                  `min-w-${avatarSize.width} w-[${avatarSize.width}px] h-[${avatarSize.height}px]`,
                )}
              >
                <NextImage
                  src={avatarSrc ?? '/images/ocs/images/profile.svg'}
                  alt={name ?? 'doctor-profile'}
                  width={avatarSize.width}
                  height={avatarSize.height}
                />
              </div>
              <div className="absolute top-2.5 right-2.5 p-[2px] flex items-center justify-center rounded-full bg-surface-primary translate-x-1/2 -translate-y-1/2">
                <span className="relative flex w-[8px] h-[8px]">
                  {isOnline && (
                    <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping" />
                  )}
                  <span
                    className={`w-[8px] h-[8px] rounded-full ${
                      isOnline ? 'bg-green-300' : 'bg-surface-tertiary'
                    }`}
                  />
                </span>
              </div>
            </div>

            <div className="flex flex-col w-full h-full justify-center">
              <div className="text-Label/Small font-bold">{name}</div>
              <div className="text-Body/xSmall text-gray-500 max-w-col line-clamp-2">
                {normalizedExpertise}
              </div>
              {medicalCodeSystem && (
                <div className="text-Body/xSmall text-gray-500">
                  کد نظام پزشکی: {medicalCodeSystem}
                </div>
              )}
              {!!clinicCities?.length && (
                <div className="flex items-center gap-x-1">
                  <div>
                    <Icon
                      name="PinCircleFill"
                      width={1}
                      height={1}
                      fill={colorPallete?.content?.tertiary}
                    />
                  </div>

                  <span className="text-Label/2xSmall text-content-tertiary">
                    {clinicCities?.map((item) => item).join(' - ')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {!!historyCount && (
            <div className="flex justify-end items-start">
              <div className="rounded-full bg-primary-500 flex justify-center items-center">
                <div className="flex justify-center items-center gap-x-1">
                  <Icon
                    name="ShieldCheck"
                    className="text-white"
                    width={1.5}
                    height={1.5}
                    fill={colors.orange[400]}
                  />

                  {historyCount && (
                    <span className="text-Body/xSmall text-nowrap">
                      {historyCount} سال سابقه
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <DoctorInfoInsurances doctorId={doctorId} />
    </div>
  );
};

export default DoctorInfo;
